import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  FileText, Upload, Calendar, AlertTriangle, CheckCircle, XCircle, 
  Clock, Mail, MailX, Settings, Download, Eye, Trash2, Bell, BellOff 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentsAlerts = ({ soundmeters }) => {
  // State for documents
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [verificationReminders, setVerificationReminders] = useState([]);
  
  // State for alerts
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [alertStats, setAlertStats] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  
  // State for equipment
  const [equipment, setEquipment] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    document_type: 'calibration_certificate',
    title: '',
    soundmeter_id: '',
    description: '',
    calibration_date: '',
    next_verification_date: '',
    tags: ''
  });
  
  // Equipment form state
  const [equipmentForm, setEquipmentForm] = useState({
    soundmeter_id: '',
    equipment_type: 'limiter',
    brand: '',
    model: '',
    serial_number: '',
    installation_date: '',
    last_calibration_date: '',
    next_calibration_due: '',
    notes: ''
  });

  useEffect(() => {
    loadDocuments();
    loadAlerts();
    loadEquipment();
    loadVerificationReminders();
    loadAlertStats();
  }, []);

  const loadDocuments = async () => {
    setLoadingDocs(true);
    try {
      const response = await axios.get(`${API}/documents/list`);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadVerificationReminders = async () => {
    try {
      const response = await axios.get(`${API}/documents/verification-reminders`);
      setVerificationReminders(response.data || []);
    } catch (error) {
      console.error('Error loading reminders:', error);
    }
  };

  const loadAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSeverity !== 'all') params.severity = filterSeverity;
      
      const response = await axios.get(`${API}/alerts/list`, { params });
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const loadAlertStats = async () => {
    try {
      const response = await axios.get(`${API}/alerts/statistics`);
      setAlertStats(response.data || {});
    } catch (error) {
      console.error('Error loading alert stats:', error);
    }
  };

  const loadEquipment = async () => {
    setLoadingEquipment(true);
    try {
      const response = await axios.get(`${API}/equipment/list`);
      setEquipment(response.data || []);
    } catch (error) {
      console.error('Error loading equipment:', error);
    } finally {
      setLoadingEquipment(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.title) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('document_type', uploadForm.document_type);
      formData.append('title', uploadForm.title);
      
      if (uploadForm.soundmeter_id) formData.append('soundmeter_id', uploadForm.soundmeter_id);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.calibration_date) formData.append('calibration_date', new Date(uploadForm.calibration_date).toISOString());
      if (uploadForm.next_verification_date) formData.append('next_verification_date', new Date(uploadForm.next_verification_date).toISOString());
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Document téléchargé avec succès !");
      
      // Reset form
      setUploadForm({
        file: null,
        document_type: 'calibration_certificate',
        title: '',
        soundmeter_id: '',
        description: '',
        calibration_date: '',
        next_verification_date: '',
        tags: ''
      });
      
      // Clear file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
      
      loadDocuments();
      loadVerificationReminders();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
      console.error(error);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    
    if (!equipmentForm.soundmeter_id || !equipmentForm.brand || !equipmentForm.model || !equipmentForm.serial_number) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const data = {
        ...equipmentForm,
        installation_date: new Date(equipmentForm.installation_date).toISOString(),
        last_calibration_date: equipmentForm.last_calibration_date ? new Date(equipmentForm.last_calibration_date).toISOString() : null,
        next_calibration_due: equipmentForm.next_calibration_due ? new Date(equipmentForm.next_calibration_due).toISOString() : null
      };

      await axios.post(`${API}/equipment/add`, data);
      toast.success("Équipement ajouté avec succès !");
      
      // Reset form
      setEquipmentForm({
        soundmeter_id: '',
        equipment_type: 'limiter',
        brand: '',
        model: '',
        serial_number: '',
        installation_date: '',
        last_calibration_date: '',
        next_calibration_due: '',
        notes: ''
      });
      
      loadEquipment();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'équipement");
      console.error(error);
    }
  };

  const respondToAlert = async (alertId, status, notes = '', action = '') => {
    try {
      await axios.post(`${API}/alerts/${alertId}/respond`, {
        alert_id: alertId,
        status: status,
        response_notes: notes,
        action_taken: action
      });

      toast.success("Réponse enregistrée");
      loadAlerts();
      loadAlertStats();
    } catch (error) {
      toast.error("Erreur lors de la réponse");
      console.error(error);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-blue-500'
    };
    return colors[severity] || 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="h-4 w-4" />,
      acknowledged: <CheckCircle className="h-4 w-4" />,
      resolved: <CheckCircle className="h-4 w-4" />,
      no_response: <MailX className="h-4 w-4" />,
      escalated: <AlertTriangle className="h-4 w-4" />
    };
    return icons[status] || <Bell className="h-4 w-4" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      acknowledged: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      no_response: 'bg-red-100 text-red-800',
      escalated: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      environmental_impact: "Étude d'impact environnementale",
      calibration_certificate: "Certificat de calibrage",
      periodic_verification: "Vérification périodique",
      compliance_certificate: "Certificat de conformité",
      technical_report: "Rapport technique",
      other: "Autre"
    };
    return labels[type] || type;
  };

  const getEquipmentTypeLabel = (type) => {
    const labels = {
      limiter: "Limiteur-enregistreur",
      sensor: "Capteur/Microphone",
      display: "Afficheur",
      controller: "Régulateur"
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="documents" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="documents">📄 Documents</TabsTrigger>
          <TabsTrigger value="alerts">🔔 Alertes</TabsTrigger>
          <TabsTrigger value="equipment">⚙️ Matériel</TabsTrigger>
          <TabsTrigger value="reminders">📅 Rappels</TabsTrigger>
        </TabsList>

        {/* DOCUMENTS TAB */}
        <TabsContent value="documents" className="space-y-6">
          {/* Upload Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Upload className="h-5 w-5" />
                <span>Télécharger un Document</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUploadDocument} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Fichier *</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                      required
                    />
                  </div>

                  <div>
                    <Label>Type de document *</Label>
                    <Select 
                      value={uploadForm.document_type} 
                      onValueChange={(val) => setUploadForm({...uploadForm, document_type: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="environmental_impact">Étude d'impact environnementale</SelectItem>
                        <SelectItem value="calibration_certificate">Certificat de calibrage</SelectItem>
                        <SelectItem value="periodic_verification">Vérification périodique</SelectItem>
                        <SelectItem value="compliance_certificate">Certificat de conformité</SelectItem>
                        <SelectItem value="technical_report">Rapport technique</SelectItem>
                        <SelectItem value="other">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Sonomètre associé</Label>
                    <Select 
                      value={uploadForm.soundmeter_id} 
                      onValueChange={(val) => setUploadForm({...uploadForm, soundmeter_id: val})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Aucun" />
                      </SelectTrigger>
                      <SelectContent>
                        {soundmeters?.map(sm => (
                          <SelectItem key={sm.soundmeter_id} value={sm.soundmeter_id}>
                            {sm.name || sm.soundmeter_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label>Titre *</Label>
                    <Input
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                      placeholder="Ex: Certificat de calibrage 2025"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={uploadForm.description}
                      onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                      placeholder="Description du document..."
                      rows={2}
                    />
                  </div>

                  {(uploadForm.document_type === 'calibration_certificate' || uploadForm.document_type === 'periodic_verification') && (
                    <>
                      <div>
                        <Label>Date de calibrage</Label>
                        <Input
                          type="date"
                          value={uploadForm.calibration_date}
                          onChange={(e) => setUploadForm({...uploadForm, calibration_date: e.target.value})}
                        />
                      </div>

                      <div>
                        <Label>Prochaine vérification</Label>
                        <Input
                          type="date"
                          value={uploadForm.next_verification_date}
                          onChange={(e) => setUploadForm({...uploadForm, next_verification_date: e.target.value})}
                        />
                        <p className="text-xs text-slate-500 mt-1">Rappel automatique 30 jours avant</p>
                      </div>
                    </>
                  )}

                  <div className="md:col-span-2">
                    <Label>Tags (séparés par des virgules)</Label>
                    <Input
                      value={uploadForm.tags}
                      onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                      placeholder="Ex: 2025, urgent, site1"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={uploadingDoc} className="bg-teal-600 hover:bg-teal-700">
                  {uploadingDoc ? 'Téléchargement...' : 'Télécharger le document'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Documents List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Documents archivés ({documents.length})</span>
                <Button variant="outline" size="sm" onClick={loadDocuments}>
                  Actualiser
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingDocs ? (
                <div className="text-center py-8 text-slate-500">Chargement...</div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Aucun document</div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="p-4 bg-slate-50 rounded-lg border hover:border-teal-500 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-5 w-5 text-teal-600" />
                            <h4 className="font-medium">{doc.title}</h4>
                            {doc.verification_due && (
                              <Badge variant="destructive" className="text-xs">
                                Vérification due
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{getDocumentTypeLabel(doc.document_type)}</p>
                          {doc.description && (
                            <p className="text-sm text-slate-500 mt-1">{doc.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                            <span>📎 {doc.file_name}</span>
                            <span>📅 {new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                            {doc.next_verification_date && (
                              <span className={doc.verification_due ? 'text-red-600 font-medium' : ''}>
                                🔔 Prochaine vérif: {new Date(doc.next_verification_date).toLocaleDateString('fr-FR')}
                                {doc.days_until_verification !== undefined && ` (${doc.days_until_verification} jours)`}
                              </span>
                            )}
                          </div>
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {doc.tags.map((tag, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ALERTS TAB */}
        <TabsContent value="alerts" className="space-y-6">
          {/* Alert Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600">{alertStats.total || 0}</div>
                  <div className="text-sm text-slate-600">Total</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-600">{alertStats.pending || 0}</div>
                  <div className="text-sm text-slate-600">En attente</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">{alertStats.no_response || 0}</div>
                  <div className="text-sm text-slate-600">Sans réponse</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{alertStats.resolved || 0}</div>
                  <div className="text-sm text-slate-600">Résolues</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <Label>Statut</Label>
                  <Select value={filterStatus} onValueChange={(val) => { setFilterStatus(val); loadAlerts(); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="acknowledged">Prise en compte</SelectItem>
                      <SelectItem value="resolved">Résolues</SelectItem>
                      <SelectItem value="no_response">Sans réponse</SelectItem>
                      <SelectItem value="escalated">Escaladées</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1">
                  <Label>Sévérité</Label>
                  <Select value={filterSeverity} onValueChange={(val) => { setFilterSeverity(val); loadAlerts(); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      <SelectItem value="critical">Critique</SelectItem>
                      <SelectItem value="high">Élevée</SelectItem>
                      <SelectItem value="medium">Moyenne</SelectItem>
                      <SelectItem value="low">Faible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts List */}
          <Card>
            <CardHeader>
              <CardTitle>Historique des alertes ({alerts.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingAlerts ? (
                <div className="text-center py-8 text-slate-500">Chargement...</div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Aucune alerte</div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div key={alert.alert_id} className="p-4 bg-white rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <Badge className={getSeverityColor(alert.severity)}>
                              {alert.severity}
                            </Badge>
                            <Badge className={getStatusColor(alert.status)}>
                              {getStatusIcon(alert.status)}
                              <span className="ml-1">{alert.status}</span>
                            </Badge>
                            {alert.email_sent && (
                              <Badge variant="outline">
                                <Mail className="h-3 w-3 mr-1" />
                                Email envoyé
                              </Badge>
                            )}
                          </div>
                          
                          <h4 className="font-medium mt-2">{alert.title}</h4>
                          <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                          
                          {alert.equipment_reference && (
                            <div className="mt-2 p-2 bg-slate-50 rounded text-sm">
                              <span className="font-medium">Matériel: </span>
                              {alert.equipment_reference}
                            </div>
                          )}
                          
                          {(alert.measured_value || alert.threshold_value) && (
                            <div className="mt-2 text-sm">
                              <span className="font-medium">Mesure: </span>
                              {alert.measured_value} dB / Seuil: {alert.threshold_value} dB
                            </div>
                          )}
                          
                          <div className="mt-2 text-xs text-slate-500">
                            🕒 {new Date(alert.triggered_at).toLocaleString('fr-FR')}
                          </div>
                          
                          {alert.response_notes && (
                            <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                              <span className="font-medium">Réponse: </span>
                              {alert.response_notes}
                            </div>
                          )}
                        </div>
                        
                        {alert.status === 'pending' && (
                          <div className="flex space-x-2 ml-4">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => respondToAlert(alert.alert_id, 'acknowledged')}
                            >
                              Prendre en compte
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => respondToAlert(alert.alert_id, 'resolved')}
                            >
                              Résoudre
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EQUIPMENT TAB */}
        <TabsContent value="equipment" className="space-y-6">
          {/* Add Equipment Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Ajouter du Matériel</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddEquipment} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Sonomètre *</Label>
                    <Select 
                      value={equipmentForm.soundmeter_id} 
                      onValueChange={(val) => setEquipmentForm({...equipmentForm, soundmeter_id: val})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        {soundmeters?.map(sm => (
                          <SelectItem key={sm.soundmeter_id} value={sm.soundmeter_id}>
                            {sm.name || sm.soundmeter_id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Type d'équipement *</Label>
                    <Select 
                      value={equipmentForm.equipment_type} 
                      onValueChange={(val) => setEquipmentForm({...equipmentForm, equipment_type: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="limiter">Limiteur-enregistreur</SelectItem>
                        <SelectItem value="sensor">Capteur/Microphone</SelectItem>
                        <SelectItem value="display">Afficheur</SelectItem>
                        <SelectItem value="controller">Régulateur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Marque *</Label>
                    <Input
                      value={equipmentForm.brand}
                      onChange={(e) => setEquipmentForm({...equipmentForm, brand: e.target.value})}
                      placeholder="Ex: Convergence Instruments"
                      required
                    />
                  </div>

                  <div>
                    <Label>Modèle *</Label>
                    <Input
                      value={equipmentForm.model}
                      onChange={(e) => setEquipmentForm({...equipmentForm, model: e.target.value})}
                      placeholder="Ex: dB Guard Pro"
                      required
                    />
                  </div>

                  <div>
                    <Label>Numéro de série *</Label>
                    <Input
                      value={equipmentForm.serial_number}
                      onChange={(e) => setEquipmentForm({...equipmentForm, serial_number: e.target.value})}
                      placeholder="Ex: CI-2025-001"
                      required
                    />
                  </div>

                  <div>
                    <Label>Date d'installation *</Label>
                    <Input
                      type="date"
                      value={equipmentForm.installation_date}
                      onChange={(e) => setEquipmentForm({...equipmentForm, installation_date: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label>Dernier calibrage</Label>
                    <Input
                      type="date"
                      value={equipmentForm.last_calibration_date}
                      onChange={(e) => setEquipmentForm({...equipmentForm, last_calibration_date: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Prochain calibrage dû</Label>
                    <Input
                      type="date"
                      value={equipmentForm.next_calibration_due}
                      onChange={(e) => setEquipmentForm({...equipmentForm, next_calibration_due: e.target.value})}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={equipmentForm.notes}
                      onChange={(e) => setEquipmentForm({...equipmentForm, notes: e.target.value})}
                      placeholder="Notes supplémentaires..."
                      rows={2}
                    />
                  </div>
                </div>

                <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                  Ajouter l'équipement
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Equipment List */}
          <Card>
            <CardHeader>
              <CardTitle>Équipements ({equipment.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingEquipment ? (
                <div className="text-center py-8 text-slate-500">Chargement...</div>
              ) : equipment.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Aucun équipement</div>
              ) : (
                <div className="space-y-3">
                  {equipment.map((eq) => (
                    <div key={eq.equipment_id} className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <Badge>{getEquipmentTypeLabel(eq.equipment_type)}</Badge>
                            {eq.calibration_due && (
                              <Badge variant="destructive">Calibrage dû</Badge>
                            )}
                          </div>
                          <h4 className="font-medium mt-2">{eq.brand} {eq.model}</h4>
                          <p className="text-sm text-slate-600">S/N: {eq.serial_number}</p>
                          <div className="mt-2 text-sm text-slate-500">
                            <div>Installation: {new Date(eq.installation_date).toLocaleDateString('fr-FR')}</div>
                            {eq.last_calibration_date && (
                              <div>Dernier calibrage: {new Date(eq.last_calibration_date).toLocaleDateString('fr-FR')}</div>
                            )}
                            {eq.next_calibration_due && (
                              <div className={eq.calibration_due ? 'text-red-600 font-medium' : ''}>
                                Prochain calibrage: {new Date(eq.next_calibration_due).toLocaleDateString('fr-FR')}
                                {eq.days_until_calibration !== undefined && ` (${eq.days_until_calibration} jours)`}
                              </div>
                            )}
                          </div>
                          {eq.notes && (
                            <p className="text-sm text-slate-500 mt-2 italic">{eq.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* REMINDERS TAB */}
        <TabsContent value="reminders" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>Rappels de Vérification ({verificationReminders.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {verificationReminders.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>Aucune vérification à venir</p>
                  <p className="text-sm mt-2">Toutes les vérifications sont à jour !</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {verificationReminders.map((reminder) => (
                    <Alert 
                      key={reminder.document_id}
                      className={
                        reminder.urgency === 'critical' ? 'border-red-500 bg-red-50' :
                        reminder.urgency === 'high' ? 'border-orange-500 bg-orange-50' :
                        reminder.urgency === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                        'border-blue-500 bg-blue-50'
                      }
                    >
                      <AlertDescription>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <AlertTriangle className={
                                reminder.urgency === 'critical' ? 'h-5 w-5 text-red-600' :
                                reminder.urgency === 'high' ? 'h-5 w-5 text-orange-600' :
                                reminder.urgency === 'medium' ? 'h-5 w-5 text-yellow-600' :
                                'h-5 w-5 text-blue-600'
                              } />
                              <h4 className="font-medium">{reminder.title}</h4>
                              {reminder.is_overdue && (
                                <Badge variant="destructive">En retard</Badge>
                              )}
                            </div>
                            <p className="text-sm mt-2">
                              {reminder.is_overdue ? 
                                `Vérification en retard de ${Math.abs(reminder.days_until_verification)} jours` :
                                `Vérification dans ${reminder.days_until_verification} jours`
                              }
                            </p>
                            <p className="text-sm text-slate-600 mt-1">
                              Date prévue: {new Date(reminder.next_verification_date).toLocaleDateString('fr-FR')}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {getDocumentTypeLabel(reminder.document_type)}
                            </p>
                          </div>
                          <Button variant="outline" size="sm">
                            Planifier
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription>
              <div className="flex items-start space-x-2">
                <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium">Rappels automatiques</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Vous recevrez des notifications par email 30 jours avant chaque échéance de vérification.
                    Les vérifications périodiques doivent être effectuées tous les 2 ans maximum selon la réglementation.
                  </p>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DocumentsAlerts;
